import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';

// Import Components
import Layout from './components/Layout';

// Import Pages
import Login from './pages/Login';
import DashboardHome from './pages/DashboardHome';
import RealEstateHub from './pages/RealEstateHub';
import ShortTermRentalsHub from './pages/ShortTermRentalsHub';
import ShortTermRentalsPropertyHub from './pages/ShortTermRentalsPropertyHub';
import { StrPropertyScope } from './features/shortTermRentals/properties';
import FinancialsPage from './features/shortTermRentals/FinancialsPage';
import BookingsPage from './features/shortTermRentals/BookingsPage';
import PlanningPage from './features/shortTermRentals/PlanningPage';
import PricingPage from './features/shortTermRentals/PricingPage';
import CleaningPage from './features/shortTermRentals/CleaningPage';
import SmartHomePage from './features/shortTermRentals/SmartHomePage';
import InventoryPage from './features/shortTermRentals/InventoryPage';
import Documentation from './pages/Documentation';
import DocumentationBrowser from './pages/DocumentationBrowser';
import FileEditor from './pages/FileEditor';
import SellingPropertyPage from './pages/SellingPropertyPage';
import MyTimeHub from './pages/MyTimeHub';
import ActivityLogPage from './pages/ActivityLogPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import TimeTrackingOverviewPage from './pages/TimeTrackingOverviewPage';
import TasksPage from './pages/TasksPage';

// Import Context
import { DocumentationProvider } from './context/DocumentationContext';

// Import Real Estate Features
import RawDataMenu from './features/realEstate/RawDataMenu';
import RawDataTable from './features/realEstate/RawDataTable';
import TransactionTable from './features/realEstate/TransactionTable'; 
import LeadGen from './features/realEstate/LeadGen'; 
import ZKTable from './features/realEstate/ZKTable'; 
import MasterSearchPage from './features/realEstate/MasterSearchPage';
import AdminDashboard from './features/realEstate/AdminDashboard'; // NEW IMPORT
import DbBackupPage from './features/realEstate/DbBackupPage';
import ContactsPage from './features/realEstate/ContactsPage';
import UserManagementPage from './features/realEstate/UserManagementPage';
import PropertiesMapPage from './pages/PropertiesMapPage';
import PropertiesLandMapPage from './pages/PropertiesLandMapPage';
import CheckedAreasMapPage from './pages/CheckedAreasMapPage';
import { getDefaultRoute, hasAnyPermission, hasPermission } from './utils/permissions';

const isPublicLandHost = window.location.hostname === 'land.dobrereal.com';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('authToken');
  return token ? children : <Navigate to="/login" />;
};

const PermissionRoute = ({ permission, anyPermissions, children }) => {
  const isAllowed = permission ? hasPermission(permission) : hasAnyPermission(anyPermissions || []);
  return isAllowed ? children : <Navigate to={getDefaultRoute()} replace />;
};

// Pricing and cleaning used to live directly under /short-term-rentals, and /short-term-rentals/cleaning
// can be saved as a user's landing page. Those paths now open the original property.
const LegacyStrPageRedirect = ({ page }) => {
  const { search } = useLocation();
  return <Navigate to={`/short-term-rentals/properties/hisicapriorehu/${page}${search}`} replace />;
};

const IndexRoute = () => {
  const defaultRoute = getDefaultRoute();
  if (defaultRoute && defaultRoute !== '/' && defaultRoute !== '/dashboard') {
    return <Navigate to={defaultRoute} replace />;
  }
  return <PermissionRoute permission="canDashboard"><DashboardHome /></PermissionRoute>;
};

function App() {
  useEffect(() => {
    if (!isPublicLandHost) {
      return;
    }

    document.title = 'Dobre Real Land';

    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.setAttribute('href', `${process.env.PUBLIC_URL || ''}/logo_dobrereal.svg`);
      favicon.setAttribute('type', 'image/svg+xml');
    }

    const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (appleIcon) {
      appleIcon.setAttribute('href', `${process.env.PUBLIC_URL || ''}/logo_dobrereal.svg`);
    }
  }, []);

  if (isPublicLandHost) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PropertiesLandMapPage isPublic />} />
          <Route path="/land-map" element={<PropertiesLandMapPage isPublic />} />
          <Route path="*" element={<Navigate to="/land-map" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
      <DocumentationProvider>
    <BrowserRouter>
      <Routes>
        {/* Public Route: Login */}
        <Route path="/login" element={<Login />} />
        <Route path="/land-map" element={<PropertiesLandMapPage isPublic />} />
        
        {/* Protected Routes: The Hub */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          
          {/* Dashboard Home */}
          <Route index element={<IndexRoute />} />
          <Route path="dashboard" element={<PermissionRoute permission="canDashboard"><DashboardHome /></PermissionRoute>} />
          <Route path="selling/:id" element={<PermissionRoute permission="canRealEstate"><SellingPropertyPage /></PermissionRoute>} />
          
          {/* Documentation Section */}
          <Route path="documentation" element={<PermissionRoute permission="canDocumentation"><Documentation /></PermissionRoute>} />
                    <Route path="documentation/:cardId" element={<PermissionRoute permission="canDocumentation"><DocumentationBrowser /></PermissionRoute>} />
                    <Route path="documentation/:cardId/file/*" element={<PermissionRoute permission="canDocumentation"><FileEditor /></PermissionRoute>} />
                    <Route path="documentation/:cardId/new" element={<PermissionRoute permission="canDocumentation"><FileEditor /></PermissionRoute>} />

          {/* My Time Section */}
          <Route path="my-time" element={<PermissionRoute permission="canMyTime"><MyTimeHub /></PermissionRoute>} />
          <Route path="my-time/activity-log" element={<PermissionRoute permission="canMyTime"><ActivityLogPage /></PermissionRoute>} />
          <Route path="my-time/time-tracking" element={<PermissionRoute permission="canTimeTracking"><TimeTrackingPage /></PermissionRoute>} />
          <Route path="my-time/time-tracking/all" element={<PermissionRoute permission="canTimeTrackingOverview"><TimeTrackingOverviewPage /></PermissionRoute>} />

          {/* Tasks: top-level section */}
          <Route path="tasks" element={<PermissionRoute permission="canMyTime"><TasksPage /></PermissionRoute>} />
          
          {/* NEW ADMIN ROUTES */}
          <Route path="admin" element={<PermissionRoute permission="canAdmin"><AdminDashboard /></PermissionRoute>} />
          <Route path="admin/db-backup" element={<PermissionRoute anyPermissions={["canAdmin", "canAdminDbBackup"]}><DbBackupPage /></PermissionRoute>} />
          <Route path="admin/users" element={<PermissionRoute anyPermissions={["canAdmin", "canUserManagement"]}><UserManagementPage /></PermissionRoute>} />
          
          {/* Real Estate Section */}
          <Route path="real-estate" element={<PermissionRoute permission="canRealEstate"><Outlet /></PermissionRoute>}>
            <Route index element={<PermissionRoute permission="canRealEstateHub"><RealEstateHub /></PermissionRoute>} />
            <Route path="lead-gen" element={<PermissionRoute permission="canLeadGen"><LeadGen /></PermissionRoute>} />
            <Route path="lead-gen/checked-areas" element={<PermissionRoute permission="canCheckedAreas"><CheckedAreasMapPage /></PermissionRoute>} />
            <Route path="contacts" element={<PermissionRoute permission="canContacts"><ContactsPage /></PermissionRoute>} />
            <Route path="properties-map" element={<PermissionRoute permission="canPropertiesMap"><PropertiesMapPage /></PermissionRoute>} />
            <Route path="land-map" element={<PermissionRoute permission="canLandMap"><PropertiesLandMapPage /></PermissionRoute>} />
            <Route path="raw" element={<PermissionRoute permission="canRawData"><RawDataMenu /></PermissionRoute>} />
            
            {/* Sales */}
            <Route path="raw/sales/posli" element={<PermissionRoute permission="canRawData"><TransactionTable endpoint="/api/sales/posli" title="Sales Transactions" /></PermissionRoute>} />
            <Route path="raw/sales/delistavb" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/sales/delistavb" title="Sales: Parts of Buildings" /></PermissionRoute>} />
            <Route path="raw/sales/zemljisca" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/sales/zemljisca" title="Sales: Land Registry" /></PermissionRoute>} />
            <Route path="raw/sales/sifranti" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/sales/sifranti" title="Sales: Reference Codes" /></PermissionRoute>} />

            {/* Rentals */}
            <Route path="raw/rentals/posli" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/rentals/posli" title="Rental Transactions" /></PermissionRoute>} />
            <Route path="raw/rentals/delistavb" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/rentals/delistavb" title="Rental: Parts of Buildings" /></PermissionRoute>} />
            <Route path="raw/rentals/sifranti" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/rentals/sifranti" title="Rental: Reference Codes" /></PermissionRoute>} />

            {/* KN */}
            <Route path="raw/kn/delistavb" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/kn/delistavb" title="KN Deli stavb" /></PermissionRoute>} />
            <Route path="raw/kn/imetniki" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/kn/imetniki" title="KN Imetniki lastnistva" /></PermissionRoute>} />
            <Route path="raw/kn/osebe" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/kn/osebe" title="KN Osebe" /></PermissionRoute>} />
            <Route path="raw/kn/naslovi" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/kn/naslovi" title="KN Naslovi HS" /></PermissionRoute>} />
            <Route path="raw/kn/stavbe" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/kn/stavbe" title="KN Stavbe" /></PermissionRoute>} />

            {/* EV (Evidenca Vrednotenja) */}
            <Route path="raw/ev/vrednotenja" element={<PermissionRoute permission="canRawData"><RawDataTable endpoint="/api/ev/vrednotenja" title="EV Vrednotenja" /></PermissionRoute>} />

            {/* ZK & Master */}
            <Route path="raw/zk" element={<PermissionRoute permission="canRawData"><ZKTable /></PermissionRoute>} />
            <Route path="master-portal" element={<PermissionRoute permission="canMasterPortal"><MasterSearchPage /></PermissionRoute>} />

          </Route>

          {/* Short Term Rentals Section */}
          <Route path="short-term-rentals" element={<PermissionRoute permission="canShortTermRentals"><Outlet /></PermissionRoute>}>
            <Route index element={<ShortTermRentalsHub />} />
            <Route path="financials" element={<PermissionRoute permission="canShortTermRentalsFinancials"><FinancialsPage /></PermissionRoute>} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="planning" element={<PlanningPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="pricing" element={<LegacyStrPageRedirect page="pricing" />} />
            <Route path="cleaning" element={<LegacyStrPageRedirect page="cleaning" />} />
            <Route path="properties/:propertyKey" element={<StrPropertyScope />}>
              <Route index element={<ShortTermRentalsPropertyHub />} />
              <Route path="financials" element={<PermissionRoute permission="canShortTermRentalsFinancials"><FinancialsPage /></PermissionRoute>} />
              <Route path="bookings" element={<BookingsPage />} />
              <Route path="planning" element={<PlanningPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="cleaning" element={<PermissionRoute permission="canShortTermRentalsCleaning"><CleaningPage /></PermissionRoute>} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="smart-home" element={<SmartHomePage />} />
            </Route>
          </Route>

        </Route>
      </Routes>
    </BrowserRouter>
    </DocumentationProvider>
  );
}

export default App;